'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';

interface User {
  id?: string;
  name: string;
  email: string;
  role?: string;
  roles?: string[]; 
  allowed_modules?: string[]; // <-- CORREÇÃO: Adicionado para o TypeScript e o contexto saberem que isso existe
  tenant: string;
  tenantId?: string; 
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  activeTenant: string | null; 
  login: (token: string, userData: User) => void;
  logout: () => void;
  switchTenant: (newTenantId: string) => void; 
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [activeTenant, setActiveTenant] = useState<string | null>(null); 
  const router = useRouter();

  useEffect(() => {
    const storedToken = Cookies.get('nexus_token');
    const storedUser = Cookies.get('nexus_user');
    const storedActiveTenant = Cookies.get('nexus_active_tenant');

    if (storedToken && storedUser) {
      setToken(storedToken);
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      
      setActiveTenant(storedActiveTenant || parsedUser.tenantId || parsedUser.tenant);
    }
  }, []);

  const login = (newToken: string, newUser: User) => {
    setToken(newToken);
    setUser(newUser);
    
    const defaultTenantId = newUser.tenantId || newUser.tenant;
    setActiveTenant(defaultTenantId);

    // CORREÇÃO DE ARQUITETURA: Adicionado path: '/' para o cookie valer no site todo
    Cookies.set('nexus_token', newToken, { expires: 1, path: '/' });
    Cookies.set('nexus_user', JSON.stringify(newUser), { expires: 1, path: '/' });
    if (defaultTenantId) {
      Cookies.set('nexus_active_tenant', defaultTenantId, { expires: 1, path: '/' });
    }

    router.push('/dashboard');
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setActiveTenant(null);
    
    // CORREÇÃO DE ARQUITETURA: Removendo da raiz
    Cookies.remove('nexus_token', { path: '/' });
    Cookies.remove('nexus_user', { path: '/' });
    Cookies.remove('nexus_active_tenant', { path: '/' }); 
    
    router.push('/');
  };

  const switchTenant = (newTenantId: string) => {
    setActiveTenant(newTenantId);
    // CORREÇÃO DE ARQUITETURA: path: '/' obriga a sobrescrever o cookie global
    Cookies.set('nexus_active_tenant', newTenantId, { expires: 1, path: '/' });
    
    window.location.href = '/dashboard'; 
  };

  return (
    <AuthContext.Provider value={{ user, token, activeTenant, login, logout, switchTenant, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);